import { useState, useEffect, useId } from 'react';
import { FlowNode } from '../../types';
import { AutoResizeTextarea } from './JsonTextarea';
import { KvRow, KvTable, objToRows, rowsToObj } from './HttpStepConfig';

const SOURCE_METHODS = ['GET', 'POST'];
const TARGET_METHODS = ['POST', 'PUT', 'PATCH'];
type TransferMode = 'multipart' | 'raw';

const MB = 1024 * 1024;
const DEFAULT_MAX_MB = 25;
const LIMIT_MAX_MB = 100;
const DEFAULT_TIMEOUT = 120;
const LIMIT_TIMEOUT = 300;
const DEFAULT_FIELD = 'file';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {};
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function parseConfig(config: Obj | undefined) {
  const source = asObj(config?.source);
  const target = asObj(config?.target);
  const multipart = asObj(target.multipart);
  const raw = asObj(target.raw);
  const maxBytes = typeof config?.maxBytes === 'number' ? config.maxBytes : null;
  const timeout = typeof config?.timeoutSeconds === 'number' ? config.timeoutSeconds : null;

  return {
    sourceUrl: str(source.url),
    sourceMethod: str(source.method) || 'GET',
    sourceHeaders: objToRows(source.headers),
    targetUrl: str(target.url),
    targetMethod: str(target.method) || 'POST',
    targetHeaders: objToRows(target.headers),
    mode: (target.mode === 'raw' ? 'raw' : 'multipart') as TransferMode,
    field: str(multipart.field) || DEFAULT_FIELD,
    fileName: str(multipart.fileName),
    multipartContentType: str(multipart.contentType),
    rawContentType: str(raw.contentType),
    maxMb: maxBytes === null ? null : maxBytes / MB,
    timeoutSeconds: timeout,
  };
}

/** JSON.stringify with sorted keys, so a stored config in another key order counts as unchanged. */
function stableStringify(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v as Obj).sort()
      .map(k => `${JSON.stringify(k)}:${stableStringify((v as Obj)[k])}`).join(',')}}`;
  }
  return JSON.stringify(v);
}

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

export function HttpTransferStepConfig({ node, onUpdate }: Props) {
  const ids = useId();
  const initial = parseConfig(node.config);
  const [label, setLabel] = useState(() => str(node.config?.label));
  const [alias, setAlias] = useState(() => str(node.config?.alias));
  const [sourceUrl, setSourceUrl] = useState(initial.sourceUrl);
  const [sourceMethod, setSourceMethod] = useState(initial.sourceMethod);
  const [sourceHeaders, setSourceHeaders] = useState<KvRow[]>(initial.sourceHeaders);
  const [targetUrl, setTargetUrl] = useState(initial.targetUrl);
  const [targetMethod, setTargetMethod] = useState(initial.targetMethod);
  const [targetHeaders, setTargetHeaders] = useState<KvRow[]>(initial.targetHeaders);
  const [mode, setMode] = useState<TransferMode>(initial.mode);
  const [field, setField] = useState(initial.field);
  const [fileName, setFileName] = useState(initial.fileName);
  const [multipartContentType, setMultipartContentType] = useState(initial.multipartContentType);
  const [rawContentType, setRawContentType] = useState(initial.rawContentType);
  const [maxMb, setMaxMb] = useState<number | null>(initial.maxMb);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number | null>(initial.timeoutSeconds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const p = parseConfig(node.config);
    setLabel(str(node.config?.label));
    setAlias(str(node.config?.alias));
    setSourceUrl(p.sourceUrl);
    setSourceMethod(p.sourceMethod);
    setSourceHeaders(p.sourceHeaders);
    setTargetUrl(p.targetUrl);
    setTargetMethod(p.targetMethod);
    setTargetHeaders(p.targetHeaders);
    setMode(p.mode);
    setField(p.field);
    setFileName(p.fileName);
    setMultipartContentType(p.multipartContentType);
    setRawContentType(p.rawContentType);
    setMaxMb(p.maxMb);
    setTimeoutSeconds(p.timeoutSeconds);
  }, [node.id]);

  useEffect(() => {
    const {
      label: _l, alias: _a, source: storedSource, target: storedTarget,
      maxBytes: _mb, timeoutSeconds: _t, ...restConfig
    } = (node.config ?? {}) as Obj;
    // Keys the panel has no field for are carried through, as in HttpStepConfig.
    const { url: _su, method: _sm, headers: _sh, ...sourceRest } = asObj(storedSource);
    const {
      url: _tu, method: _tm, headers: _th, mode: _md, multipart: storedMultipart, raw: storedRaw,
      ...targetRest
    } = asObj(storedTarget);
    const { field: _f, fileName: _fn, contentType: _mct, ...multipartRest } = asObj(storedMultipart);
    const { contentType: _rct, ...rawRest } = asObj(storedRaw);

    const srcHeaders = rowsToObj(sourceHeaders);
    const tgtHeaders = rowsToObj(targetHeaders);

    const source = {
      ...sourceRest,
      ...(sourceUrl.trim() ? { url: sourceUrl } : {}),
      method: sourceMethod,
      ...(Object.keys(srcHeaders).length > 0 ? { headers: srcHeaders } : {}),
    };

    const multipart = {
      ...multipartRest,
      field: field.trim() || DEFAULT_FIELD,
      ...(fileName.trim() ? { fileName } : {}),
      ...(multipartContentType.trim() ? { contentType: multipartContentType.trim() } : {}),
    };
    const raw = {
      ...rawRest,
      ...(rawContentType.trim() ? { contentType: rawContentType.trim() } : {}),
    };

    const target = {
      ...targetRest,
      ...(targetUrl.trim() ? { url: targetUrl } : {}),
      method: targetMethod,
      ...(Object.keys(tgtHeaders).length > 0 ? { headers: tgtHeaders } : {}),
      mode,
      // Only the active mode's block is written; the other one would be ignored by the worker.
      ...(mode === 'multipart' ? { multipart } : {}),
      ...(mode === 'raw' && Object.keys(raw).length > 0 ? { raw } : {}),
    };

    const maxBytes = maxMb === null
      ? null
      : Math.round(Math.min(Math.max(maxMb, 0), LIMIT_MAX_MB) * MB);

    const next = {
      ...restConfig,
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(alias.trim() ? { alias: alias.trim() } : {}),
      source,
      target,
      ...(maxBytes !== null ? { maxBytes } : {}),
      ...(timeoutSeconds !== null ? { timeoutSeconds: Math.min(timeoutSeconds, LIMIT_TIMEOUT) } : {}),
    };
    if (stableStringify(next) === stableStringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, alias, sourceUrl, sourceMethod, sourceHeaders, targetUrl, targetMethod, targetHeaders,
      mode, field, fileName, multipartContentType, rawContentType, maxMb, timeoutSeconds]);

  const inputClass = 'border border-input rounded p-1.5 text-xs bg-background';
  const sectionClass = 'flex flex-col gap-2 border border-input rounded p-2';

  return (
    <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
      {/* Label */}
      <div className="flex flex-col gap-1">
        <label htmlFor={`${ids}-label`} className="text-xs text-muted-foreground font-medium">Label</label>
        <input
          id={`${ids}-label`}
          type="text"
          className={inputClass}
          placeholder="e.g. Copy attachment"
          value={label}
          onChange={e => setLabel(e.target.value)}
        />
      </div>

      {/* Alias */}
      <div className="flex flex-col gap-1">
        <label htmlFor={`${ids}-alias`} className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          id={`${ids}-alias`}
          type="text"
          className={`${inputClass} font-mono`}
          placeholder="e.g. copy-attachment"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
      </div>

      {/* Source */}
      <section className={sectionClass} aria-label="Source">
        <p className="text-xs font-medium">Source</p>
        <div className="flex gap-2">
          <select
            aria-label="Source method"
            className={`${inputClass} font-medium`}
            value={sourceMethod}
            onChange={e => setSourceMethod(e.target.value)}
          >
            {SOURCE_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <AutoResizeTextarea
            aria-label="Source URL"
            className={`flex-1 ${inputClass} font-mono`}
            placeholder="{{signed-url.body.url}}"
            value={sourceUrl}
            onChange={e => setSourceUrl(e.target.value)}
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1 font-medium">Headers</p>
          <KvTable
            rows={sourceHeaders}
            setRows={setSourceHeaders}
            keyPlaceholder="Authorization"
            valuePlaceholder="Bearer {{env.SOURCE_TOKEN}}"
          />
          <p className="text-xs text-muted-foreground mt-1">
            <code>Authorization</code> is allowed. Leave it out for pre-signed URLs &mdash; the
            signature is in the URL, and many storage services reject an extra auth header.
          </p>
        </div>
      </section>

      {/* Target */}
      <section className={sectionClass} aria-label="Target">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium">Target</p>
          <div className="flex rounded border border-input overflow-hidden text-xs" role="group" aria-label="Upload mode">
            {(['multipart', 'raw'] as const).map(m => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={`px-2 py-0.5 transition-colors ${
                  mode === m
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
              >
                {m === 'multipart' ? 'Multipart' : 'Raw body'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <select
            aria-label="Target method"
            className={`${inputClass} font-medium`}
            value={targetMethod}
            onChange={e => setTargetMethod(e.target.value)}
          >
            {TARGET_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <AutoResizeTextarea
            aria-label="Target URL"
            className={`flex-1 ${inputClass} font-mono`}
            placeholder="https://api.example.com/issues/{{body.id}}/attachments"
            value={targetUrl}
            onChange={e => setTargetUrl(e.target.value)}
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1 font-medium">Headers</p>
          <KvTable
            rows={targetHeaders}
            setRows={setTargetHeaders}
            keyPlaceholder="Authorization"
            valuePlaceholder="Bearer {{env.TARGET_TOKEN}}"
          />
          {mode === 'multipart' && (
            <p className="text-xs text-muted-foreground mt-1">
              A <code>Content-Type</code> header is ignored in multipart mode &mdash; the step sets
              it, including the boundary.
            </p>
          )}
        </div>

        {mode === 'multipart' ? (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor={`${ids}-field`} className="text-xs text-muted-foreground font-medium">Field name</label>
              <input
                id={`${ids}-field`}
                type="text"
                className={`${inputClass} font-mono`}
                placeholder={DEFAULT_FIELD}
                value={field}
                onChange={e => setField(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={`${ids}-filename`} className="text-xs text-muted-foreground font-medium">File name</label>
              <input
                id={`${ids}-filename`}
                type="text"
                className={`${inputClass} font-mono`}
                placeholder="from source if empty"
                value={fileName}
                onChange={e => setFileName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1 col-span-2">
              <label htmlFor={`${ids}-mp-ct`} className="text-xs text-muted-foreground font-medium">File content type</label>
              <input
                id={`${ids}-mp-ct`}
                type="text"
                className={`${inputClass} font-mono`}
                placeholder="from source if empty"
                value={multipartContentType}
                onChange={e => setMultipartContentType(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <label htmlFor={`${ids}-raw-ct`} className="text-xs text-muted-foreground font-medium">Content type</label>
            <input
              id={`${ids}-raw-ct`}
              type="text"
              className={`${inputClass} font-mono`}
              placeholder="from source if empty"
              value={rawContentType}
              onChange={e => setRawContentType(e.target.value)}
            />
          </div>
        )}
      </section>

      {/* Limits */}
      <section className={sectionClass} aria-label="Limits">
        <p className="text-xs font-medium">Limits</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={`${ids}-max`} className="text-xs text-muted-foreground font-medium">Max size (MB)</label>
            <input
              id={`${ids}-max`}
              type="number"
              min={1}
              max={LIMIT_MAX_MB}
              placeholder={String(DEFAULT_MAX_MB)}
              className={inputClass}
              value={maxMb ?? ''}
              onChange={e => setMaxMb(e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`${ids}-timeout`} className="text-xs text-muted-foreground font-medium">Timeout (s)</label>
            <input
              id={`${ids}-timeout`}
              type="number"
              min={1}
              max={LIMIT_TIMEOUT}
              placeholder={String(DEFAULT_TIMEOUT)}
              className={inputClass}
              value={timeoutSeconds ?? ''}
              onChange={e => setTimeoutSeconds(e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          At most {LIMIT_MAX_MB} MB and {LIMIT_TIMEOUT} s. The timeout covers download and upload
          together; a larger file fails the step instead of being cut off.
        </p>
      </section>
    </div>
  );
}
